
import React from 'react';
import { EstateAssets, DebtLiability } from '../../../types';
import { CreditCard, Trash2, Plus } from 'lucide-react';

interface Props {
    data: EstateAssets;
    onChange: (assets: EstateAssets) => void;
}

export const DebtsStep: React.FC<Props> = ({ data, onChange }) => {
    const items = data.debts || [];

    const updateItem = (index: number, changes: Partial<DebtLiability>) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], ...changes };
        onChange({ ...data, debts: newItems });
    };

    const addItem = (preset?: Partial<DebtLiability>) => {
        const newItem: DebtLiability = {
            id: `debt_${Date.now()}`,
            label: '',
            type: null,
            institution: '',
            estimated_value: null,
            estimated_amount: null,
            ...preset,
        };
        onChange({ ...data, debts: [...items, newItem] });
    };

    // §6A — Funeral costs quick-entry. Skips the type dropdown for a common case.
    const hasFuneralEntry = items.some(d => d.type === 'funeral');
    const addFuneralEntry = () => addItem({ type: 'funeral', label: 'Funeral costs' });

    const removeItem = (index: number) => {
        const newItems = items.filter((_, i) => i !== index);
        onChange({ ...data, debts: newItems });
    };

    React.useEffect(() => {
        if (items.length === 0) addItem();
    }, []);

    const DEBT_TYPES = [
        { id: 'credit_card', label: 'Credit Card' },
        { id: 'loan', label: 'Personal Loan' },
        { id: 'tax', label: 'Unpaid Tax' },
        { id: 'care_fees', label: 'Care Home Fees' },
        { id: 'funeral', label: 'Funeral Costs' },
        { id: 'other', label: 'Other' },
    ];

    return (
        <div className="max-w-2xl mx-auto space-y-8">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-ink">Unsecured Debts & Liabilities</h2>
                <p className="text-ink3 mt-2">
                    Only include unsecured debts owed by the deceased. Mortgages are captured in the property section.
                </p>
                <p className="text-ink3 mt-1 text-sm">
                    Include any money owed to individuals — family, friends, or informal lenders — alongside cards, loans, care fees and unpaid tax.
                </p>
            </div>

            <div className="space-y-4">
                {items.map((item, idx) => (
                    <div key={item.id} className="bg-white p-5 rounded-xl border border-rule relative group">
                        {items.length > 1 && (
                            <button
                                onClick={() => removeItem(idx)}
                                className="absolute top-4 right-4 text-sub hover:text-ink2 transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}

                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                    <label className="block text-xs font-bold text-ink3 uppercase mb-1">Type</label>
                                    <select
                                        className="w-full border-b border-rule focus:border-accent focus:outline-none py-1 text-ink font-medium bg-transparent"
                                        value={item.type || ''}
                                        onChange={(e) => updateItem(idx, { type: e.target.value as any })}
                                    >
                                        <option value="" disabled>Select Type...</option>
                                        {DEBT_TYPES.map(t => (
                                            <option key={t.id} value={t.id}>{t.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex-1">
                                    <label className="block text-xs font-bold text-ink3 uppercase mb-1">Provider (Optional)</label>
                                    <div className="flex items-center gap-2">
                                        <CreditCard className="w-5 h-5 text-ink3" />
                                        <input
                                            type="text"
                                            placeholder="e.g. Halifax"
                                            className="w-full border-b border-rule focus:border-accent focus:outline-none py-1 text-ink font-medium"
                                            value={item.institution || ''}
                                            onChange={(e) => updateItem(idx, { institution: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-ink3 uppercase mb-1">Amount Owed (£)</label>
                                <input
                                    type="number"
                                    className="w-full sm:w-1/2 border rounded px-3 py-2 text-sm focus:ring-1 focus:ring-accent focus:outline-none"
                                    placeholder="0.00"
                                    value={item.estimated_amount ?? item.estimated_value ?? ''}
                                    onChange={(e) => {
                                        const val = e.target.value ? Number(e.target.value) : null;
                                        updateItem(idx, { estimated_amount: val, estimated_value: val });
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ))}

                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={() => addItem()}
                        className="flex-1 py-3 border-2 border-dashed border-rule rounded-lg text-ink3 font-bold hover:border-accent hover:text-accent transition-all flex items-center justify-center gap-2 text-sm"
                    >
                        <Plus className="w-4 h-4" /> Add Another Debt
                    </button>
                    {!hasFuneralEntry && (
                        <button
                            onClick={addFuneralEntry}
                            className="sm:flex-1 py-3 border border-rule rounded-lg text-ink3 font-medium hover:border-accent hover:text-accent transition-all flex items-center justify-center gap-2 text-sm"
                            title="Add a pre-filled funeral costs entry"
                        >
                            <Plus className="w-4 h-4" /> Add funeral costs
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
